import React from "react";

interface HighlightProps {
    /** The text to render. Null/undefined renders nothing. */
    text: string | null | undefined;
    /** Filter term to mark. Empty term renders the text untouched. */
    term: string;
}

/**
 * Wraps every case-insensitive occurrence of `term` inside `text` in `<mark class="fhl">`.
 *
 * Splits with `indexOf` rather than a RegExp — the term is raw user input, so building a
 * pattern from it would need escaping and would still be the wrong tool here. Only ever
 * used on searched text fields (codes, names, descriptions, lot paths); never on money,
 * hours or percentages, which must stay visually stable while filtering.
 */
const Highlight: React.FC<HighlightProps> = ({ text, term }) => {
    const src = text ?? "";
    const needle = term.trim().toLowerCase();
    if (!needle || !src) return <>{src}</>;

    const hay = src.toLowerCase();
    const parts: React.ReactNode[] = [];
    let cursor = 0;
    let key = 0;

    for (;;) {
        const at = hay.indexOf(needle, cursor);
        if (at < 0) break;
        if (at > cursor) parts.push(src.slice(cursor, at));
        parts.push(
            <mark className="fhl" key={key++}>
                {src.slice(at, at + needle.length)}
            </mark>
        );
        cursor = at + needle.length;
    }

    if (cursor === 0) return <>{src}</>;
    if (cursor < src.length) parts.push(src.slice(cursor));
    return <>{parts}</>;
};

export default Highlight;
